import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";

/**
 * Represents a tempo change at a specific point in musical time.
 * The clock uses a list of these segments to handle dynamic tempo changes.
 */
interface TempoSegment {
  /** Beat number where this tempo starts */
  startBeat: number;
  /** Beats per minute for this segment */
  bpm: number;
  /** Audio context time when this segment starts */
  startAudioTime: number;
}

/**
 * Musical timing and tempo management with support for dynamic tempo changes.
 *
 * Acts as the central timekeeper for the entire music system, providing
 * precise synchronization between all musical components. Maintains a list
 * of tempo segments to handle smooth tempo changes and provides conversion
 * between musical beats and audio context time.
 *
 * Key features:
 * - Beat/measure tracking with fractional precision
 * - Dynamic tempo changes with segment-based scheduling
 * - Audio latency compensation for visual synchronization
 * - Event emission for beat and measure boundaries with scheduling lookahead
 * - Conversion utilities between musical and audio time
 */
export class TempoClock extends BaseEntity implements Entity {
  tags = ["tempoClock"];
  id = "tempoClock";
  pausable = false; // Keep music timing during pause

  private static readonly SCHEDULER_LOOKAHEAD = 0.1; // 100ms lookahead for scheduling
  private beatsPerMeasure: number = 4;
  private tempoSegments: TempoSegment[] = [];
  private lastScheduledBeat: number = -1;
  private lastProcessedBeat: number = -1;

  constructor(initialBpm: number = 80) {
    super();
    // Initialize with first tempo segment - will be set to proper audio time in onAdd
    this.tempoSegments.push({
      startBeat: 0,
      bpm: initialBpm,
      startAudioTime: 0, // Will be updated in onAdd when audio context is available
    });
  }

  /**
   * Initialize timing when added to game (when audio context is available).
   */
  onAdd() {
    // Update the initial tempo segment with the current audio context time
    if (this.tempoSegments.length > 0) {
      this.tempoSegments[0].startAudioTime = this.audioContext.currentTime;
    }
  }

  get audioContext(): AudioContext {
    const audio = this.game?.audio;
    if (!audio) {
      throw new Error("Audio context not yet available in game");
    }
    return audio;
  }

  /**
   * Get the current beat number with fractional precision.
   * Compensates for audio latency to keep visuals synchronized.
   *
   * @returns Current beat (e.g., 1.25 = quarter way through beat 2)
   */
  getCurrentBeatNumber(): number {
    const visualLatencyCompensation = this.audioContext.baseLatency;
    const currentTime =
      this.audioContext.currentTime - visualLatencyCompensation;
    return this.getBeatNumberAtTime(currentTime);
  }

  /**
   * Get the current measure number (0-based)
   */
  getMeasureNumber(): number {
    return Math.floor(this.getCurrentBeatNumber() / this.beatsPerMeasure);
  }

  /**
   * Get the current beat within the measure (0-3 for 4/4 time)
   */
  getBeatInMeasure(): number {
    return this.getCurrentBeatNumber() % this.beatsPerMeasure;
  }

  /**
   * Get the current tempo in BPM
   */
  getCurrentTempo(): number {
    const currentBeat = this.getCurrentBeatNumber();
    const segment = this.getSegmentForBeat(currentBeat);
    if (!segment) {
      console.warn(
        "TempoClock: No segment found for current tempo, returning default 120 BPM",
      );
      return 120; // Default fallback tempo
    }
    return segment.bpm;
  }

  /**
   * Convert a beat number to audio context time.
   * Handles tempo changes by finding the appropriate segment
   * and calculating within that segment's timing.
   */
  getAudioTimeForBeat(beatNumber: number): number {
    const segment = this.getSegmentForBeat(beatNumber);
    if (!segment) {
      console.warn(
        "TempoClock: No segment found for beat, returning current audio time",
      );
      return this.audioContext.currentTime;
    }

    const beatsFromSegmentStart = beatNumber - segment.startBeat;
    const secondsFromSegmentStart = (beatsFromSegmentStart * 60) / segment.bpm;
    const audioTime = segment.startAudioTime + secondsFromSegmentStart;

    return audioTime;
  }

  /**
   * Get the audio context time for a subdivision of a beat
   */
  getAudioTimeForSubdivision(
    beatNumber: number,
    subdivision: number,
    totalSubdivisions: number,
  ): number {
    const beatStartTime = this.getAudioTimeForBeat(beatNumber);
    const beatDuration = 60 / this.getCurrentTempo();
    const subdivisionDuration = beatDuration / totalSubdivisions;
    return beatStartTime + subdivision * subdivisionDuration;
  }

  /**
   * Convert beats to duration in seconds at current tempo.
   * Alias method for compatibility with old instrument system.
   */
  beatsToSeconds(beats: number): number {
    const currentTempo = this.getCurrentTempo();
    return (beats * 60) / currentTempo;
  }

  /**
   * Convert beat number to audio context time.
   * Alias method for compatibility with old instrument system.
   */
  beatToTime(beatNumber: number): number {
    return this.getAudioTimeForBeat(beatNumber);
  }

  /**
   * Change tempo immediately at the next beat boundary.
   * Creates a new tempo segment starting at the next whole beat.
   */
  setTempo(bpm: number): void {
    const currentBeat = Math.ceil(this.getCurrentBeatNumber());
    this.scheduleTempo(bpm, currentBeat);
  }

  /**
   * Schedule a tempo change to occur at a specific beat.
   * Removes any conflicting future tempo changes and adds
   * a new segment at the specified beat.
   */
  scheduleTempo(bpm: number, atBeat: number): void {
    const oldBpm = this.getCurrentTempo();

    // Remove any existing segments that start at or after this beat
    this.tempoSegments = this.tempoSegments.filter(
      (seg) => seg.startBeat < atBeat,
    );

    // Add new tempo segment
    this.tempoSegments.push({
      startBeat: atBeat,
      bpm: bpm,
      startAudioTime: this.getAudioTimeForBeat(atBeat),
    });

    // Sort segments by start beat to maintain order
    this.tempoSegments.sort((a, b) => a.startBeat - b.startBeat);

    // Emit tempo change event
    this.dispatch("tempoChange", {
      oldBpm,
      newBpm: bpm,
      changeBeat: atBeat,
    });
  }

  /**
   * Get beat number at a specific audio time
   */
  private getBeatNumberAtTime(audioTime: number): number {
    // Safety check - ensure we have at least one tempo segment
    if (this.tempoSegments.length === 0) {
      console.warn("TempoClock: No tempo segments found, returning beat 0");
      return 0;
    }

    // Find the appropriate tempo segment
    let currentSegment = this.tempoSegments[0];
    for (const segment of this.tempoSegments) {
      if (segment.startAudioTime <= audioTime) {
        currentSegment = segment;
      } else {
        break;
      }
    }

    // Additional safety check
    if (!currentSegment) {
      console.warn(
        "TempoClock: No valid tempo segment found, returning beat 0",
      );
      return 0;
    }

    // Calculate beat number within this segment
    const timeFromSegmentStart = audioTime - currentSegment.startAudioTime;
    const beatsFromSegmentStart =
      (timeFromSegmentStart * currentSegment.bpm) / 60;
    return currentSegment.startBeat + beatsFromSegmentStart;
  }

  /**
   * Get the tempo segment that contains a specific beat
   */
  private getSegmentForBeat(beatNumber: number): TempoSegment | undefined {
    // Safety check for empty segments
    if (this.tempoSegments.length === 0) {
      console.warn("TempoClock: No tempo segments available");
      return undefined;
    }

    let currentSegment = this.tempoSegments[0];
    for (const segment of this.tempoSegments) {
      if (segment.startBeat <= beatNumber) {
        currentSegment = segment;
      } else {
        break;
      }
    }
    return currentSegment;
  }

  /**
   * Called every frame to emit scheduling and synchronization events.
   * Uses lookahead scheduling to give composers time to prepare audio events.
   */
  onTick(): void {
    // Check for beats that need to be scheduled with lookahead
    const lookaheadTime =
      this.audioContext.currentTime + TempoClock.SCHEDULER_LOOKAHEAD;
    const lookaheadBeat = Math.floor(this.getBeatNumberAtTime(lookaheadTime));

    if (lookaheadBeat > this.lastScheduledBeat) {
      // We need to schedule this upcoming beat
      const measureNumber = Math.floor(lookaheadBeat / this.beatsPerMeasure);
      const beatInMeasure = lookaheadBeat % this.beatsPerMeasure;
      const beatStartTime = this.getAudioTimeForBeat(lookaheadBeat);

      // Emit scheduling event for composers to prepare audio
      this.dispatch("scheduleBeat", {
        beatNumber: lookaheadBeat,
        measureNumber,
        beatInMeasure,
        beatStartTime, // Exact audio time when beat will occur
      });

      // Emit measure scheduling event on downbeats
      if (beatInMeasure === 0) {
        this.dispatch("scheduleMeasure", {
          measureNumber,
          beatNumber: lookaheadBeat,
          measureStartTime: beatStartTime,
        });
      }

      this.lastScheduledBeat = lookaheadBeat;
    }

    // Also emit synchronization events for visual elements (no lookahead)
    const currentBeat = Math.floor(this.getCurrentBeatNumber());
    if (currentBeat > (this.lastProcessedBeat ?? -1)) {
      const measureNumber = Math.floor(currentBeat / this.beatsPerMeasure);
      const beatInMeasure = currentBeat % this.beatsPerMeasure;

      // Emit beat event for visual synchronization
      this.dispatch("beatStart", {
        beatNumber: currentBeat,
        measureNumber,
        beatInMeasure,
      });

      // Emit measure event on downbeats
      if (beatInMeasure === 0) {
        this.dispatch("measureStart", {
          measureNumber,
          beatNumber: currentBeat,
        });
      }

      this.lastProcessedBeat = currentBeat;
    }
  }

  /**
   * Get how close we are to a beat (0 = exactly on beat, 1 = just before next beat)
   */
  getBeatPhase(time?: number): number {
    const currentTime = time ?? this.audioContext.currentTime;
    const beatNumber = this.getBeatNumberAtTime(currentTime);
    return beatNumber % 1.0;
  }

  /**
   * Check if we're currently on a downbeat (first beat of measure)
   */
  isDownbeat(time?: number): boolean {
    const currentTime = time ?? this.audioContext.currentTime;
    const beatNumber = this.getBeatNumberAtTime(currentTime);
    const nearestBeat = Math.round(beatNumber);
    return nearestBeat % this.beatsPerMeasure === 0;
  }
}
